/* /app/annuaire — Module Annuaire (contacts & organisations en arborescence).
 *
 * Layout split : à gauche la barre de filtres + arborescence collapsible,
 * à droite le panneau détail de l'entité sélectionnée ou l'empty state.
 *
 * Fonctionnalités :
 *   - CRUD entités via modal
 *   - Drag de noeuds non supporté en v1 (édition du parent via formulaire)
 *   - Import : modal qui accepte PDF ou collage texte, NOAM propose un arbre,
 *     utilisateur valide pour insertion.
 *   - Filtres + recherche live.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { api, apiUpload } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import DocumentAttachZone from "@/components/DocumentAttachZone";
import HybridResearchPanel from "@/components/HybridResearchPanel";
import Carto360Button from "@/components/Carto360Button";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import SalesArgumentsPanel from "@/components/SalesArgumentsPanel";
import FudswaccaPanel from "@/components/FudswaccaPanel";
import EntityConnectivityPanel from "@/components/EntityConnectivityPanel";
import EntityGraphPanel from "@/components/EntityGraphPanel";
import OntologyExplorerButton from "@/components/OntologyExplorerButton";
import PresentationStudioModal from "@/components/PresentationStudioModal";
import { Presentation as PresentationIcon } from "lucide-react";
import AnnuaireCollisionCanvas from "@/components/AnnuaireCollisionCanvas";
import OriginationEngine from "@/components/OriginationEngine";
import AnnuaireMindmap from "@/components/AnnuaireMindmap";
import AnnuaireSPLC from "@/components/AnnuaireSPLC";
import DedupReview from "@/components/DedupReview";
import InlineGuide from "@/components/InlineGuide";
import ProactiveWhisper from "@/components/ProactiveWhisper";
import { ShareToChatButton } from "@/components/ShareToChat";
import { CreditRatingBadge } from "@/components/CreditRating";
import {
  ReactFlow, Background, Controls, MiniMap, Handle, Position, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import {
  Network, Plus, Search, Filter, ChevronRight, ChevronDown, Trash2, Edit3, X,
  Save, Loader2, Building2, User, Landmark, Coins, Users2, Heart, ShieldCheck,
  Globe2, Upload, Sparkles, FileText, ArrowLeft, Tag, CheckCircle2, AlertTriangle,
  List, GitBranch, Crown, Atom, Waypoints,
} from "lucide-react";

const TYPE_ICONS = {
  holding: Landmark,
  societe: Building2,
  personne: User,
  institution: ShieldCheck,
  fonds: Coins,
  famille: Heart,
  ong: Users2,
  etat: Globe2,
};

const STATUS_COLORS = {
  actif: "#1D9E75",
  dormant: "#888",
  prospect: "#378ADD",
  partenaire: "#EF9F27",
};

export default function Annuaire() {
  const { t } = useTranslation();
  const [tree, setTree] = useState([]);
  const [flatCount, setFlatCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState({ types: [], statuses: [], sectors: [], countries: [], modules: [] });
  const [filters, setFilters] = useState({ search: "", type: "", status: "", sector: "", country: "" });
  const [annuaireView, setAnnuaireView] = useState("all"); // V23.14.4 — all | active | dormant
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createPrefill, setCreatePrefill] = useState(null);
  const [importing, setImporting] = useState(false);

  // Connect Your Reality — ouverture directe du modal d'import via ?import=1
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("import") === "1") setImporting(true);
  }, []);
  const [viewMode, setViewMode] = useState("list"); // "list" | "graph"

  // V23.15.21 — Prefill par le Copilote IA (sessionStorage)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("noam_prefill_annuaire");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      sessionStorage.removeItem("noam_prefill_annuaire");
      if (parsed && parsed.data && typeof parsed.data === "object") {
        setCreatePrefill(parsed.data);
        setCreating(true);
      }
    } catch (e) {
      // ignore corrupted prefill
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (annuaireView && annuaireView !== "all") params.set("view", annuaireView);
      const r = await api.get(`/workspace/annuaire/tree?${params.toString()}`);
      setTree(r.data.tree || []);
      setFlatCount(r.data.total || 0);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  }, [filters, annuaireView]);

  useEffect(() => {
    api.get("/workspace/annuaire/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="annuaire-root">
      <ProactiveWhisper module="annuaire" />
      <ThreeQPageHeader endpoint="/workspace/annuaire/top-priority" />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[#378ADD] text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5">
            <Network className="w-3 h-3" /> {t("annuaire.overline", { defaultValue: "Stratégique" })}
          </div>
          <h1 className="noam-display text-3xl font-bold mt-1">
            {t("annuaire.title", { defaultValue: "Annuaire" })}
          </h1>
          <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
            {t("annuaire.subtitle", { defaultValue: "Contacts et organisations organisés en arborescence vivante — holdings, filiales, personnes par catégorie." })}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <ModuleHelpButton moduleKey="annuaire" />
          <DedupReview onChanged={load} />
          <div className="inline-flex rounded-md border border-white/10 bg-black/30 p-0.5" data-testid="annuaire-view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`text-[11px] px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "list" ? "bg-[#378ADD]/20 text-[#378ADD]" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="annuaire-view-list"
            >
              <List className="w-3 h-3" /> {t("annuaire.view_list", { defaultValue: "Liste" })}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("graph")}
              className={`text-[11px] px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "graph" ? "bg-[#378ADD]/20 text-[#378ADD]" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="annuaire-view-graph"
            >
              <GitBranch className="w-3 h-3" /> {t("annuaire.view_graph", { defaultValue: "Arborescence" })}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("mindmap")}
              className={`text-[11px] px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "mindmap" ? "bg-[#7F77DD]/20 text-[#7F77DD]" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="annuaire-view-mindmap"
              title="Carte mentale : représentation en branches (racine → secteurs → acteurs)"
            >
              <Waypoints className="w-3 h-3" /> {t("annuaire.view_mindmap", { defaultValue: "Carte mentale" })}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("collision")}
              className={`text-[11px] px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "collision" ? "bg-[#EF9F27]/20 text-[#EF9F27]" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="annuaire-view-collision"
              title="Mode Collision : les entités dérivent, l'IA détecte les adjacences factuelles cachées"
            >
              <Atom className="w-3 h-3" /> {t("annuaire.view_collision", { defaultValue: "Collision" })}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("splc")}
              className={`text-[11px] px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "splc" ? "bg-[#378ADD]/20 text-[#378ADD]" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="annuaire-view-splc"
              title="SPLC — Chaîne de valeur : fournisseurs à gauche, clients à droite (inspiré Bloomberg Supply Chain)"
            >
              <GitBranch className="w-3 h-3 rotate-90" /> SPLC
            </button>
            <button
              type="button"
              onClick={() => setViewMode("origination")}
              className={`text-[11px] px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "origination" ? "bg-[#1D9E75]/20 text-[#1D9E75]" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="annuaire-view-origination"
              title="Moteur d'origination : écosystèmes présumés, chemins d'introduction et matrice d'influence"
            >
              <Crown className="w-3 h-3" /> {t("annuaire.view_origination", { defaultValue: "Origination" })}
            </button>
          </div>
          <button onClick={() => setImporting(true)} className="btn-secondary text-xs" data-testid="annuaire-import-btn">
            <Upload className="w-3.5 h-3.5" /> {t("annuaire.import", { defaultValue: "Importer (PDF/texte)" })}
          </button>
          <button onClick={() => setCreating(true)} className="btn-primary text-xs" data-testid="annuaire-new-btn">
            <Plus className="w-3.5 h-3.5" /> {t("annuaire.new_entity", { defaultValue: "Nouvelle entité" })}
          </button>
        </div>
      </div>

      <FiltersBar filters={filters} setFilters={setFilters} catalog={catalog} totalCount={flatCount} />

      <InlineGuide
        id="annuaire-intro"
        title="Ici, vous construisez votre cartographie des acteurs"
        bullets={[
          "Recenser vos clients, partenaires, concurrents et fournisseurs en une seule vue.",
          "Tagger les acteurs stratégiques en « Super Partenaire » pour activer les analyses Fudswacca + arguments commerciaux.",
          "Ouvrir une fiche pour voir la Cartographie des dépendances, les signaux récents et les recommandations d'engagement.",
        ]}
        hint="Conseil : commencez par 3-5 entités clés. Vous pourrez en ajouter d'autres au fil de l'eau."
      />

      {/* V23.14.6 — Cercle vertueux : pont stratégie → action */}
      <CercleVertueuxActionBanner
        onShowList={() => {
          setViewMode("list");
          setAnnuaireView("all");
          setFilters((f) => ({ ...f, search: "cercle_vertueux_potentiel" }));
        }}
      />

      {/* V23.14.4 — Filtre activité (Pareto) */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="annuaire-view-tabs">
        <span className="text-[11px] uppercase tracking-wider text-zinc-500">{t("annuaire.activity_filter", { defaultValue: "Activité" })}</span>
        {["active", "all", "dormant"].map((v) => (
          <button
            key={v}
            onClick={() => setAnnuaireView(v)}
            className={`text-xs px-3 py-1 rounded border transition ${
              annuaireView === v
                ? "border-[#378ADD] bg-[#378ADD]/10 text-[#378ADD]"
                : "border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
            }`}
            data-testid={`annuaire-view-${v}-btn`}
          >
            {v === "active"
              ? t("annuaire.view_active", { defaultValue: "Actives" })
              : v === "dormant"
              ? t("annuaire.view_dormant", { defaultValue: "Dormantes" })
              : t("annuaire.view_all", { defaultValue: "Toutes" })}
          </button>
        ))}
        <span className="text-[11px] text-zinc-500 ml-1">
          {t("annuaire.activity_count", {
            count: flatCount,
            defaultValue: `${flatCount} entité${flatCount > 1 ? "s" : ""}`,
          })}
        </span>
      </div>

      {viewMode === "origination" ? (
        <OriginationEngine />
      ) : viewMode === "splc" ? (
        <AnnuaireSPLC />
      ) : viewMode === "mindmap" ? (
        <div className="space-y-4">
          <div className="noam-card p-0 overflow-hidden" style={{ height: 680 }} data-testid="annuaire-mindmap-panel">
            {loading ? (
              <div className="p-6 text-zinc-500 text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}
              </div>
            ) : tree.length === 0 ? (
              <EmptyState
                icon={Network}
                title={t("annuaire.empty_title", { defaultValue: "Annuaire vide" })}
                desc={t("annuaire.empty_desc", { defaultValue: "Créez une première entité ou importez un document pour amorcer votre annuaire." })}
                cta={t("annuaire.new_entity", { defaultValue: "Nouvelle entité" })}
                onCta={() => setCreating(true)}
                testid="annuaire-empty-mindmap"
              />
            ) : (
              <AnnuaireMindmap tree={tree} selectedId={selectedId} onSelect={setSelectedId} onChanged={load} />
            )}
          </div>
          {selectedId && (
            <div data-testid="annuaire-detail-panel-mindmap">
              <EntityDetail
                key={selectedId}
                id={selectedId}
                catalog={catalog}
                onClose={() => setSelectedId(null)}
                onChanged={() => { load(); }}
                onDeleted={() => { setSelectedId(null); load(); }}
              />
            </div>
          )}
        </div>
      ) : viewMode === "collision" ? (
        <div className="space-y-4">
          <div className="noam-card p-0 overflow-hidden" data-testid="annuaire-collision-panel">
            {loading ? (
              <div className="p-6 text-zinc-500 text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}
              </div>
            ) : tree.length === 0 ? (
              <EmptyState
                icon={Network}
                title={t("annuaire.empty_title", { defaultValue: "Annuaire vide" })}
                desc={t("annuaire.empty_desc", { defaultValue: "Créez une première entité ou importez un document pour amorcer votre annuaire." })}
                cta={t("annuaire.new_entity", { defaultValue: "Nouvelle entité" })}
                onCta={() => setCreating(true)}
                testid="annuaire-empty-collision"
              />
            ) : (
              <AnnuaireCollisionCanvas
                entities={flattenTree(tree)}
                onSelectEntity={setSelectedId}
              />
            )}
          </div>
          {selectedId && (
            <div data-testid="annuaire-detail-panel-collision">
              <EntityDetail
                key={selectedId}
                id={selectedId}
                catalog={catalog}
                onClose={() => setSelectedId(null)}
                onChanged={() => { load(); }}
                onDeleted={() => { setSelectedId(null); load(); }}
              />
            </div>
          )}
        </div>
      ) : viewMode === "graph" ? (
        <div className="space-y-4">
          <div className="noam-card p-0 overflow-hidden" style={{ height: 640 }} data-testid="annuaire-graph-panel">
            {loading ? (
              <div className="p-6 text-zinc-500 text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}
              </div>
            ) : tree.length === 0 ? (
              <EmptyState
                icon={Network}
                title={t("annuaire.empty_title", { defaultValue: "Annuaire vide" })}
                desc={t("annuaire.empty_desc", { defaultValue: "Créez une première entité ou importez un document pour amorcer votre annuaire." })}
                cta={t("annuaire.new_entity", { defaultValue: "Nouvelle entité" })}
                onCta={() => setCreating(true)}
                testid="annuaire-empty-graph"
              />
            ) : (
              <AnnuaireGraph
                tree={tree}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            )}
          </div>
          {selectedId && (
            <div data-testid="annuaire-detail-panel-graph">
              <EntityDetail
                key={selectedId}
                id={selectedId}
                catalog={catalog}
                onClose={() => setSelectedId(null)}
                onChanged={() => { load(); }}
                onDeleted={() => { setSelectedId(null); load(); }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 noam-card p-3 min-h-[400px]" data-testid="annuaire-tree-panel">
            {loading ? (
              <div className="p-6 text-zinc-500 text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}
              </div>
            ) : tree.length === 0 ? (
              <EmptyState
                icon={Network}
                title={t("annuaire.empty_title", { defaultValue: "Annuaire vide" })}
                desc={t("annuaire.empty_desc", { defaultValue: "Créez une première entité ou importez un document pour amorcer votre annuaire." })}
                cta={t("annuaire.new_entity", { defaultValue: "Nouvelle entité" })}
                onCta={() => setCreating(true)}
                testid="annuaire-empty"
              />
            ) : (
              <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1">
                {tree.map((node) => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    depth={0}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-3" data-testid="annuaire-detail-panel">
            {selectedId ? (
              <EntityDetail
                key={selectedId}
                id={selectedId}
                catalog={catalog}
                onClose={() => setSelectedId(null)}
                onChanged={() => { load(); }}
                onDeleted={() => { setSelectedId(null); load(); }}
              />
            ) : (            <div className="noam-card p-8 text-center text-zinc-500 text-sm">
                <Network className="w-8 h-8 text-[#378ADD] mx-auto opacity-50 mb-3" />
                {t("annuaire.select_node", { defaultValue: "Sélectionnez une entité dans l'arborescence pour voir le détail." })}
              </div>
            )}
          </div>
        </div>
      )}

      {creating && (
        <EntityModal
          catalog={catalog}
          prefill={createPrefill}
          onClose={() => { setCreating(false); setCreatePrefill(null); }}
          onSaved={(id) => { setCreating(false); setCreatePrefill(null); load(); setSelectedId(id); }}
        />
      )}
      {importing && (
        <ImportModal
          onClose={() => setImporting(false)}
          onConfirmed={() => { setImporting(false); load(); }}
        />
      )}
    </div>
  );
}

/* ============ GRAPH VIEW (Arborescence) ============ */

/* Layout tidy-tree gauche → droite. On parcourt l'arbre en profondeur ; les
 * feuilles obtiennent un Y séquentiel (espacement vertical fixe), les noeuds
 * internes se centrent verticalement entre leur premier et leur dernier enfant. */
const NODE_W = 240;
const NODE_H = 64;
const H_SPACING = 320; // distance horizontale entre niveaux
const V_SPACING = 88;  // distance verticale entre feuilles

/** V23.15.60 — Aplatit l'arborescence en liste d'entités pour le mode Collision.
 *  Limite à 20 entités max (au-delà : le canvas devient illisible et brûle le quota LLM). */
function flattenTree(tree, limit = 20) {
  const out = [];
  const walk = (nodes) => {
    for (const n of nodes || []) {
      if (out.length >= limit) return;
      out.push({
        id: n.id,
        name: n.name,
        type: n.type,
        sector: n.sector,
        status: n.status,
      });
      if (n.children && n.children.length) walk(n.children);
    }
  };
  walk(tree);
  return out;
}

function layoutTree(tree) {
  const nodes = [];
  const edges = [];
  let yCursor = 0;

  const walk = (node, depth, parentId, offsetY = 0) => {
    const children = node.children || [];
    let y;
    if (children.length === 0) {
      y = offsetY + yCursor * V_SPACING;
      yCursor += 1;
    } else {
      const childYs = children.map((c) => walk(c, depth + 1, node.id, offsetY));
      y = (childYs[0] + childYs[childYs.length - 1]) / 2;
    }
    nodes.push({
      id: node.id,
      type: "annuaire",
      position: { x: depth * H_SPACING, y },
      data: {
        name: node.name,
        nodeType: node.type,
        status: node.status,
        childrenCount: children.length,
      },
      draggable: true,
    });
    if (parentId) {
      edges.push({
        id: `e-${parentId}-${node.id}`,
        source: parentId,
        target: node.id,
        type: "smoothstep",
        style: { stroke: "#378ADD66", strokeWidth: 1.5 },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#378ADD99", width: 14, height: 14 },
      });
    }
    return y;
  };

  // 1) Racines AVEC enfants : tidy-tree classique, empilées verticalement.
  const rootsWithChildren = tree.filter((r) => (r.children || []).length > 0);
  for (const root of rootsWithChildren) {
    walk(root, 0, null);
    yCursor += 0.5; // léger espace inter-racines
  }

  // 2) Racines orphelines (sans enfants) : grille compacte sous l'arbre pour
  //    éviter une colonne verticale interminable quand on importe 100+ contacts.
  const orphans = tree.filter((r) => (r.children || []).length === 0);
  if (orphans.length > 0) {
    const ORPHAN_COLS = 5;
    const baseY = yCursor * V_SPACING + (rootsWithChildren.length > 0 ? V_SPACING : 0);
    orphans.forEach((node, i) => {
      const col = i % ORPHAN_COLS;
      const row = Math.floor(i / ORPHAN_COLS);
      nodes.push({
        id: node.id,
        type: "annuaire",
        position: { x: col * H_SPACING, y: baseY + row * V_SPACING },
        data: {
          name: node.name,
          nodeType: node.type,
          status: node.status,
          childrenCount: 0,
        },
        draggable: true,
      });
    });
  }

  return { nodes, edges };
}

function AnnuaireNode({ data, selected }) {
  const Icon = TYPE_ICONS[data.nodeType] || Building2;
  const statusColor = STATUS_COLORS[data.status] || "#888";
  return (
    <div
      className={`rounded-md bg-[#0E0E10] border transition-colors px-3 py-2 shadow-md ${
        selected ? "border-[#378ADD] ring-1 ring-[#378ADD]/40" : "border-white/10 hover:border-[#378ADD]/40"
      }`}
      style={{ width: NODE_W, minHeight: NODE_H }}
      data-testid={`annuaire-graph-node-${data.nodeType}`}
    >
      <Handle type="target" position={Position.Left} style={{ background: "#378ADD", width: 6, height: 6, border: "none" }} />
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded bg-[#378ADD]/10 border border-[#378ADD]/30 flex items-center justify-center shrink-0">
          <Icon className="w-3.5 h-3.5 text-[#378ADD]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] text-zinc-100 font-medium truncate leading-tight">{data.name}</div>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400 uppercase tracking-wider">
              {data.nodeType}
            </span>
            <span
              className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase"
              style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}55` }}
            >
              {data.status}
            </span>
            {data.childrenCount > 0 && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30">
                {data.childrenCount}
              </span>
            )}
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Right} style={{ background: "#378ADD", width: 6, height: 6, border: "none" }} />
    </div>
  );
}

const annuaireNodeTypes = { annuaire: AnnuaireNode };

function AnnuaireGraph({ tree, selectedId, onSelect }) {
  const { nodes, edges } = useMemo(() => layoutTree(tree), [tree]);
  // Inject `selected` flag into nodes so the custom renderer can highlight.
  const styledNodes = useMemo(
    () => nodes.map((n) => ({ ...n, selected: n.id === selectedId })),
    [nodes, selectedId]
  );
  return (
    <ReactFlow
      nodes={styledNodes}
      edges={edges}
      nodeTypes={annuaireNodeTypes}
      onNodeClick={(_, node) => onSelect(node.id)}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      colorMode="dark"
      proOptions={{ hideAttribution: true }}
      nodesDraggable
      nodesConnectable={false}
      elementsSelectable
      minZoom={0.2}
      maxZoom={1.5}
    >
      <Background gap={20} color="#1f2937" />
      <Controls showInteractive={false} />
      <MiniMap
        pannable
        zoomable
        nodeColor={() => "#378ADD"}
        maskColor="rgba(0,0,0,0.6)"
        style={{ background: "#0E0E10", border: "1px solid rgba(255,255,255,0.1)" }}
      />
    </ReactFlow>
  );
}

/* ============ FILTERS ============ */

function FiltersBar({ filters, setFilters, catalog, totalCount }) {
  const { t } = useTranslation();
  return (
    <div className="noam-card p-3 flex flex-wrap items-center gap-2 text-xs" data-testid="annuaire-filters">
      <div className="relative flex-1 min-w-[180px]">
        <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          placeholder={t("annuaire.search_placeholder", { defaultValue: "Recherche (nom, description, tag)…" })}
          className="w-full bg-black/30 border border-white/10 rounded-md pl-7 pr-3 py-1.5 text-white focus:border-[#378ADD] focus:outline-none"
          data-testid="annuaire-search-input"
        />
      </div>
      <Filter className="w-3.5 h-3.5 text-zinc-500" />
      <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
        className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white" data-testid="annuaire-filter-type">
        <option value="">{t("annuaire.all_types", { defaultValue: "Tous types" })}</option>
        {(catalog.types || []).map((tt) => <option key={tt} value={tt}>{tt}</option>)}
      </select>
      <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white" data-testid="annuaire-filter-status">
        <option value="">{t("annuaire.all_statuses", { defaultValue: "Tous statuts" })}</option>
        {(catalog.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select value={filters.sector} onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
        className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white">
        <option value="">{t("annuaire.all_sectors", { defaultValue: "Tous secteurs" })}</option>
        {(catalog.sectors || []).map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })}
        className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white">
        <option value="">{t("annuaire.all_countries", { defaultValue: "Tous pays" })}</option>
        {(catalog.countries || []).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <span className="text-zinc-500 ml-auto">{totalCount} {t("annuaire.entities_count", { defaultValue: "entité(s)" })}</span>
    </div>
  );
}

/* ============ TREE ============ */

function TreeNode({ node, depth, selectedId, onSelect }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const Icon = TYPE_ICONS[node.type] || Building2;
  const isSelected = node.id === selectedId;
  const hasChildren = (node.children || []).length > 0;
  const statusColor = STATUS_COLORS[node.status] || "#888";

  return (
    <div data-testid={`annuaire-node-${node.id}`}>
      <div
        onClick={() => onSelect(node.id)}
        className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors group ${
          isSelected ? "bg-[#378ADD]/15 border border-[#378ADD]/40" : "border border-transparent hover:bg-white/5"
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            className="text-zinc-500 hover:text-white p-0.5"
            data-testid={`annuaire-node-toggle-${node.id}`}
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : <span className="w-4" />}
        <Icon className="w-3.5 h-3.5 text-[#378ADD] shrink-0" />
        <span className={`text-sm flex-1 truncate ${isSelected ? "text-white font-medium" : "text-zinc-200"}`}>{node.name}</span>
        <span
          className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Carto360Button subjectType="entite" subjectId={node.id} compact placement="row" />
        </span>
        <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase shrink-0"
          style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}55` }}>
          {node.status}
        </span>
      </div>
      {expanded && hasChildren && (
        <div>
          {(node.children || []).map((c) => (
            <TreeNodeRecursive key={c.id} node={c} depth={depth + 1} selectedId={selectedId} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
}

// Indirect reference so the babel visual-edits plugin does not walk a static cycle.
const TreeNodeRecursive = TreeNode;

/* ============ DETAIL ============ */

function EntityDetail({ id, catalog, onClose, onChanged, onDeleted }) {
  const { t } = useTranslation();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [presenting, setPresenting] = useState(false); // Studio de présentation

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/workspace/annuaire/entities/${id}`); setEntity(r.data); }
    catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    const cascade = (entity?.children || []).length > 0;
    const msg = cascade
      ? t("annuaire.confirm_delete_cascade", { defaultValue: `Supprimer "${entity?.name}" ET ses ${entity.children.length} sous-entité(s) ?` })
      : t("annuaire.confirm_delete", { defaultValue: `Supprimer "${entity?.name}" ?` });
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/workspace/annuaire/entities/${id}${cascade ? "?cascade=true" : ""}`);
      notify.success(t("annuaire.deleted", { defaultValue: "Entité supprimée." }));
      onDeleted();
    } catch (e) { notify.error(fmtError(e)); }
  };

  if (loading) return <div className="noam-card p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}</div>;
  if (!entity) return null;

  if (editing) {
    return (
      <EntityForm
        entity={entity}
        catalog={catalog}
        onCancel={() => setEditing(false)}
        onSaved={async () => { setEditing(false); await load(); onChanged(); }}
      />
    );
  }

  const Icon = TYPE_ICONS[entity.type] || Building2;
  const statusColor = STATUS_COLORS[entity.status] || "#888";

  return (
    <div className="noam-card p-5 space-y-4" data-testid="annuaire-detail-card">
      {presenting && (
        <PresentationStudioModal
          subject={{ type: "entity", id, label: entity.name }}
          onClose={() => setPresenting(false)}
        />
      )}
      {entity.parent && (
        <div className="text-[11px] text-zinc-500">
          <ArrowLeft className="w-3 h-3 inline mr-1" />
          {t("annuaire.parent", { defaultValue: "Sous" })} : <span className="text-zinc-300">{entity.parent.name}</span>
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-md bg-[#378ADD]/10 border border-[#378ADD]/30 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-[#378ADD]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="noam-display text-xl font-bold truncate" data-testid="annuaire-detail-name">{entity.name}</h2>
              <CreditRatingBadge entityId={id} />
              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase shrink-0"
                style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}55` }}>
                {entity.status}
              </span>
            </div>
            <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-2">
              <span className="px-1.5 py-0.5 rounded bg-white/5">{entity.type}</span>
              {entity.sector && <span>· {entity.sector}</span>}
              {entity.country && <span>· {entity.country}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setPresenting(true)} className="btn-secondary text-xs text-zinc-400 hover:text-[#7F77DD]" title="Studio de présentation" data-testid="annuaire-present-btn">
            <PresentationIcon className="w-3 h-3" /> Présenter
          </button>
          <Carto360Button subjectType="entite" subjectId={id} compact />
          <ShareToChatButton
            attachment={{ kind: "annuaire", id }}
            title={entity.name}
            className="btn-secondary text-xs text-zinc-400 hover:text-[#1D9E75]"
          />
          <OntologyExplorerButton otype="entity" oid={entity.id} label={entity.name} rootLabel="Annuaire" />
          <button onClick={() => setEditing(true)} className="btn-secondary text-xs" data-testid="annuaire-detail-edit">
            <Edit3 className="w-3 h-3" /> {t("common.edit", { defaultValue: "Modifier" })}
          </button>
          <button onClick={remove} className="btn-secondary text-xs text-red-400 hover:text-red-300" data-testid="annuaire-detail-delete">
            <Trash2 className="w-3 h-3" />
          </button>
          <button onClick={onClose} className="btn-ghost text-xs"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* V23.14.6f — Super Partenaire : raccourci vers la Cartographie des
          dépendances de cet acteur. L'objectif : identifier les acteurs /
          systèmes / processus dont le Super Partenaire dépend, pour repérer
          où on peut s'insérer dans sa chaîne de valeur. */}
      {(entity.tags || []).some((tg) => ["cercle_vertueux_potentiel", "super_partenaire", "super_client"].includes(tg)) && (
        <div className="rounded-md border border-[#EFD127]/30 bg-gradient-to-r from-[#EFD127]/[0.06] to-transparent p-3 flex items-start gap-3" data-testid="annuaire-super-partner-cta">
          <div className="w-8 h-8 rounded bg-[#EFD127]/15 border border-[#EFD127]/40 flex items-center justify-center shrink-0">
            <Network className="w-4 h-4 text-[#EFD127]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-100 flex items-center gap-2 flex-wrap">
              {t("annuaire.super_partner_cta_title", { defaultValue: "Super Partenaire" })}
              {(entity.bras_arme_score != null) && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EFD127]/20 text-[#EFD127] font-bold" data-testid="annuaire-bras-arme-score">
                  {entity.bras_arme_score}/100
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-400 mt-0.5">
              {t("annuaire.super_partner_cta_desc", {
                defaultValue: "Cartographiez les dépendances de cet acteur (acteurs, systèmes, processus dont il dépend) pour identifier où vous pouvez vous positionner dans sa chaîne de valeur.",
              })}
            </div>
            {entity.bras_arme_notes && (
              <div className="text-[11px] text-zinc-500 mt-1 italic line-clamp-2">« {entity.bras_arme_notes} »</div>
            )}
          </div>
          <a
            href={`/app/utility-mapping?focus_entity=${encodeURIComponent(entity.id)}&focus_name=${encodeURIComponent(entity.name || "")}`}
            className="btn-primary text-xs shrink-0 self-center"
            data-testid="annuaire-super-partner-utility-map-btn"
          >
            <Network className="w-3 h-3" />
            {t("annuaire.super_partner_cta_btn", { defaultValue: "Cartographier ses dépendances" })}
          </a>
        </div>
      )}

      {/* V23.14.7e — Arguments commerciaux auto Noam (croisement méta-asymétries × profil Super Partenaire) */}
      {(entity.tags || []).some((tg) => ["cercle_vertueux_potentiel", "super_partenaire", "super_client"].includes(tg)) && (
        <>
          <SalesArgumentsPanel entityId={entity.id} entityName={entity.name} />
          <FudswaccaPanel entityId={entity.id} entityName={entity.name} />
        </>
      )}

      {/* V23.14.17-19 — Connexions cross-module (signaux récents, actifs rattachés, compteurs de refs) */}
      <EntityConnectivityPanel entityId={entity.id} entityName={entity.name} />

      {/* V23.14.22 — Graphe de connaissances visuel (D3-like via ReactFlow radial) */}
      <EntityGraphPanel entityId={entity.id} entityName={entity.name} />

      {entity.description && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("annuaire.description", { defaultValue: "Description" })}</div>
          <p className="text-sm text-zinc-300 whitespace-pre-wrap">{entity.description}</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {entity.needs && (
          <div className="rounded-md bg-white/[0.02] border border-white/5 p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] mb-1">{t("annuaire.needs", { defaultValue: "Besoins identifiés" })}</div>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{entity.needs}</p>
          </div>
        )}
        {entity.next_step && (
          <div className="rounded-md bg-white/[0.02] border border-white/5 p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#378ADD] mb-1">{t("annuaire.next_step", { defaultValue: "Prochaine étape" })}</div>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{entity.next_step}</p>
          </div>
        )}
      </div>

      {(entity.linked_modules || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("annuaire.linked", { defaultValue: "Modules Noam liés" })}</div>
          <div className="flex flex-wrap gap-1.5">
            {entity.linked_modules.map((m) => {
              // Resolve to a human label from the catalog when available.
              let label = m;
              for (const grp of (catalog.module_groups || [])) {
                const it = grp.items.find((x) => x.key === m);
                if (it) { label = it.label; break; }
              }
              return (
                <span key={m} className="text-xs px-2 py-0.5 rounded bg-[#378ADD]/15 text-[#378ADD] border border-[#378ADD]/30">{label}</span>
              );
            })}
          </div>
        </div>
      )}

      {(entity.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {entity.tags.map((tg) => {
            const canonical = ["client_direct", "relais_reseau_potentiel", "apporteur_affaires", "partenaire"].includes(tg);
            const label = tg.replace(/_/g, " ");
            return (
              <span
                key={tg}
                className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
                  canonical
                    ? "bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/40 font-semibold"
                    : "bg-white/5 text-zinc-400"
                }`}
              >
                <Tag className="w-2.5 h-2.5" /> {label}
              </span>
            );
          })}
        </div>
      )}

      {(entity.contacts || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{t("annuaire.contacts", { defaultValue: "Contacts" })}</div>
          <div className="space-y-1.5">
            {entity.contacts.map((c, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded bg-white/[0.02] text-xs">
                <User className="w-3 h-3 text-zinc-500 mt-0.5" />
                <div className="flex-1">
                  <div className="text-zinc-200">{c.name} {c.role && <span className="text-zinc-500">· {c.role}</span>}</div>
                  {(c.email || c.phone) && <div className="text-zinc-500 font-mono text-[11px]">{[c.email, c.phone].filter(Boolean).join(" · ")}</div>}
                  {c.notes && <div className="text-zinc-400 text-[11px] mt-0.5">{c.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(entity.children || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            {t("annuaire.subentities", { defaultValue: "Sous-entités" })} ({entity.children.length})
          </div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {entity.children.map((c) => (
              <span key={c.id} className="px-2 py-0.5 rounded bg-white/5 text-zinc-300">{c.name}</span>
            ))}
          </div>
        </div>
      )}

      {entity.notes && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("annuaire.notes", { defaultValue: "Notes" })}</div>
          <p className="text-sm text-zinc-300 whitespace-pre-wrap">{entity.notes}</p>
        </div>
      )}
    </div>
  );
}

/* ============ FORM ============ */

function EntityModal({ catalog, onClose, onSaved, parentId = null, prefill = null }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="annuaire-create-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg">
        <EntityForm
          entity={null}
          parentId={parentId}
          catalog={catalog}
          prefill={prefill}
          onCancel={onClose}
          onSaved={onSaved}
        />
      </div>
    </div>
  );
}

function EntityForm({ entity, catalog, parentId = null, prefill = null, onCancel, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!entity;
  const p = prefill || {};
  const [form, setForm] = useState({
    name: entity?.name ?? p.name ?? "",
    type: entity?.type ?? p.type ?? "societe",
    sector: entity?.sector ?? p.sector ?? "",
    country: entity?.country ?? p.country ?? "",
    status: entity?.status ?? p.status ?? "actif",
    parent_id: entity?.parent_id || parentId || "",
    description: entity?.description ?? p.description ?? "",
    needs: entity?.needs ?? p.needs ?? "",
    next_step: entity?.next_step ?? p.next_step ?? "",
    linked_modules: entity?.linked_modules || [],
    tags: Array.isArray(entity?.tags) ? entity.tags : (Array.isArray(p.tags) ? p.tags : []),
    notes: entity?.notes ?? p.notes ?? "",
  });
  const [parents, setParents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/workspace/annuaire/entities").then((r) => {
      const items = (r.data.items || []).filter((e) => e.id !== entity?.id);
      setParents(items);
    }).catch(() => {});
  }, [entity?.id]);

  const toggleModule = (m) => {
    setForm((f) => {
      const has = f.linked_modules.includes(m);
      return { ...f, linked_modules: has ? f.linked_modules.filter((x) => x !== m) : [...f.linked_modules, m] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        parent_id: form.parent_id || null,
        tags: Array.isArray(form.tags) ? form.tags : [],
      };
      let id;
      if (isEdit) {
        const r = await api.patch(`/workspace/annuaire/entities/${entity.id}`, payload);
        id = r.data.id;
      } else {
        const r = await api.post("/workspace/annuaire/entities", payload);
        id = r.data.id;
      }
      notify.success(isEdit ? t("annuaire.saved", { defaultValue: "Enregistré." }) : t("annuaire.created", { defaultValue: "Entité créée." }));
      onSaved(id);
    } catch (e) { setErr(fmtError(e)); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="noam-card p-5 space-y-3 max-h-[88vh] overflow-y-auto" data-testid="annuaire-form">
      <div className="flex items-center justify-between">
        <div className="noam-display text-lg font-semibold">
          {isEdit ? t("annuaire.edit", { defaultValue: "Modifier l'entité" }) : t("annuaire.new_entity", { defaultValue: "Nouvelle entité" })}
        </div>
        <button type="button" onClick={onCancel} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      {prefill && !isEdit && (
        <div className="px-3 py-2 rounded-md bg-[#EF9F27]/10 border border-[#EF9F27]/30 text-[11px] text-[#EF9F27]" data-testid="annuaire-form-prefill-banner">
          Pré-rempli par l&apos;IA Copilote — vérifiez et ajustez avant de créer.
        </div>
      )}
      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder={t("annuaire.field_name", { defaultValue: "Nom *" })} className="noam-input" data-testid="annuaire-form-name" />
      <div className="grid grid-cols-2 gap-2">
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="noam-input" data-testid="annuaire-form-type">
          {(catalog.types || []).map((tt) => <option key={tt} value={tt}>{tt}</option>)}
        </select>
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="noam-input" data-testid="annuaire-form-status">
          {(catalog.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {/* V23.15.76 — Pastilles de relation cumulables (indépendantes du status) */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
          {t("annuaire.field_relation_tags", { defaultValue: "Pastilles de relation (cumulables)" })}
        </label>
        <div className="flex flex-wrap gap-1.5" data-testid="annuaire-form-relation-tags">
          {(catalog.relation_tags || []).map((tag) => {
            const checked = (form.tags || []).includes(tag);
            const label = tag.replace(/_/g, " ");
            return (
              <button
                type="button"
                key={tag}
                onClick={() => {
                  const current = form.tags || [];
                  const next = checked ? current.filter((x) => x !== tag) : [...current, tag];
                  setForm({ ...form, tags: next });
                }}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  checked
                    ? "bg-[#EF9F27]/20 border-[#EF9F27] text-[#EF9F27] font-semibold"
                    : "bg-black/30 border-white/10 text-zinc-400 hover:border-[#EF9F27]/50 hover:text-white"
                }`}
                data-testid={`annuaire-form-tag-${tag}`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="text-[10px] text-zinc-500 mt-1">
          {t("annuaire.relation_tags_help", { defaultValue: "Une entité peut cumuler plusieurs pastilles (ex : client direct + apporteur d'affaires)." })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="noam-input">
          <option value="">{t("annuaire.field_sector", { defaultValue: "Secteur" })}</option>
          {(catalog.sectors || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="noam-input">
          <option value="">{t("annuaire.field_country", { defaultValue: "Pays" })}</option>
          {(catalog.countries || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{t("annuaire.field_parent", { defaultValue: "Entité parente (arborescence)" })}</label>
        <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className="noam-input" data-testid="annuaire-form-parent">
          <option value="">{t("annuaire.no_parent", { defaultValue: "— Racine (sans parent) —" })}</option>
          {parents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="text-[10px] text-zinc-500 mt-1">{t("annuaire.parent_help", { defaultValue: "Plan Enterprise requis pour l'arborescence multi-niveaux." })}</div>
      </div>
      <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
        placeholder={t("annuaire.field_description", { defaultValue: "Description libre" })} className="noam-input resize-none" />
      <div className="grid grid-cols-2 gap-2">
        <textarea value={form.needs} onChange={(e) => setForm({ ...form, needs: e.target.value })} rows={2}
          placeholder={t("annuaire.field_needs", { defaultValue: "Besoins identifiés" })} className="noam-input resize-none" />
        <textarea value={form.next_step} onChange={(e) => setForm({ ...form, next_step: e.target.value })} rows={2}
          placeholder={t("annuaire.field_next_step", { defaultValue: "Prochaine étape" })} className="noam-input resize-none" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
          {t("annuaire.field_linked", { defaultValue: "Modules Noam liés" })}
          {form.linked_modules.length > 0 && (
            <span className="ml-2 text-[#378ADD] normal-case tracking-normal">({form.linked_modules.length} sélectionné(s))</span>
          )}
        </label>
        {(catalog.module_groups && catalog.module_groups.length > 0) ? (
          <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
            {catalog.module_groups.map((grp) => (
              <div key={grp.group}>
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 font-semibold mb-1">{grp.group}</div>
                <div className="flex flex-wrap gap-1.5">
                  {grp.items.map((m) => {
                    const active = form.linked_modules.includes(m.key);
                    return (
                      <button type="button" key={m.key} onClick={() => toggleModule(m.key)}
                        title={m.key}
                        data-testid={`annuaire-link-${m.key}`}
                        className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                          active ? "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/40"
                                 : "bg-white/5 text-zinc-400 border-white/10 hover:border-white/20"
                        }`}>{m.label}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {(catalog.modules || []).map((m) => {
              const active = form.linked_modules.includes(m);
              return (
                <button type="button" key={m} onClick={() => toggleModule(m)}
                  className={`text-xs px-2 py-0.5 rounded border transition-colors capitalize ${
                    active ? "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/40"
                           : "bg-white/5 text-zinc-400 border-white/10 hover:border-white/20"
                  }`}>{m}</button>
              );
            })}
          </div>
        )}
      </div>
      {/* V23.15.76 — Tags libres additionnels (en plus des pastilles canoniques ci-dessus) */}
      <div>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
          {t("annuaire.field_tags_free", { defaultValue: "Tags libres additionnels" })}
        </label>
        <input
          value={(form.tags || []).filter((t) => !(catalog.relation_tags || []).includes(t)).join(", ")}
          onChange={(e) => {
            const freeTags = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
            const canonicalTags = (form.tags || []).filter((t) => (catalog.relation_tags || []).includes(t));
            setForm({ ...form, tags: [...canonicalTags, ...freeTags] });
          }}
          placeholder={t("annuaire.field_tags_free_placeholder", { defaultValue: "Ex : vip, à revoir, priorité (séparés par virgule)" })}
          className="noam-input"
          data-testid="annuaire-form-tags-free"
        />
      </div>
      <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
        placeholder={t("annuaire.field_notes", { defaultValue: "Notes" })} className="noam-input resize-none" />
      {isEdit && entity?.id && (
        <DocumentAttachZone module="annuaire" entityId={entity.id} entityLabel={entity.name} compact />
      )}
      {isEdit && entity?.id && (
        <HybridResearchPanel
          contextModule="annuaire"
          contextId={entity.id}
          initialTopic={entity.name || ""}
          defaultMode="hybrid"
          title={`Recherche hybride — ${entity.name || "entité"}`}
          testid="annuaire-hybrid-research"
          compact
        />
      )}
      {err && (
        <div className="text-xs text-[#FCA5A5] p-2 rounded border border-[#E24B4A]/30 bg-[#E24B4A]/10 flex items-start gap-2">
          <AlertTriangle className="w-3 h-3 mt-0.5" /> {err}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
        <button type="button" onClick={onCancel} className="btn-ghost text-xs">{t("common.cancel", { defaultValue: "Annuler" })}</button>
        <button type="submit" disabled={busy || !form.name.trim()} className="btn-primary text-xs disabled:opacity-50" data-testid="annuaire-form-submit">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {isEdit ? t("common.save", { defaultValue: "Enregistrer" }) : t("common.create", { defaultValue: "Créer" })}
        </button>
      </div>
    </form>
  );
}

/* ============ IMPORT ============ */

function ImportModal({ onClose, onConfirmed }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(null); // { elapsed, diagnostics } during polling

  const analyze = async () => {
    setBusy(true); setErr(""); setPreview(null); setProgress(null);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      // Démarre un job asynchrone (évite le timeout 60s de l'ingress k8s
      // pour les fichiers volumineux qui demandent plusieurs appels NOAM).
      const start = await apiUpload("/workspace/annuaire/import/start", fd, {
        params: text.trim() ? { raw_text: text.trim() } : {},
      });
      const jobId = start.data.job_id;
      setProgress({ elapsed: 0, diagnostics: start.data.diagnostics });

      // Polling — toutes les 2 secondes pendant 5 minutes max
      const startedAt = Date.now();
      let finished = false;
      while (!finished && (Date.now() - startedAt) < 300000) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 2000));
        // eslint-disable-next-line no-await-in-loop
        const status = await api.get(`/workspace/annuaire/import/status/${jobId}`);
        if (status.data.status === "done") {
          setPreview(status.data);
          finished = true;
        } else if (status.data.status === "error") {
          setErr(status.data.error || "Erreur lors de l'analyse.");
          finished = true;
        } else {
          setProgress({
            elapsed: status.data.elapsed_seconds,
            diagnostics: status.data.diagnostics,
          });
        }
      }
      if (!finished) setErr("Délai dépassé (5 min). Réessayez avec un fichier plus court ou contactez le support.");
    } catch (e) { setErr(fmtError(e)); }
    setBusy(false);
    setProgress(null);
  };

  const confirm = async () => {
    if (!preview || preview.entities.length === 0) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/workspace/annuaire/import/confirm", { entities: preview.entities });
      notify.success(t("annuaire.imported", { defaultValue: `${r.data.created} entité(s) importée(s).` }));
      onConfirmed();
    } catch (e) { setErr(fmtError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="annuaire-import-modal">
      <div onClick={(e) => e.stopPropagation()} className="noam-card w-full max-w-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="noam-overline flex items-center gap-1.5 text-[#EF9F27]">
              <Sparkles className="w-3 h-3" /> {t("annuaire.import_overline", { defaultValue: "Import assisté par IA" })}
            </div>
            <div className="noam-display text-lg font-semibold mt-1">
              {t("annuaire.import_title", { defaultValue: "Importer un document" })}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {!preview ? (
          <>
            <p className="text-xs text-zinc-400">
              {t("annuaire.import_help", { defaultValue: "Glissez un fichier (PDF, Excel .xlsx, Word .docx, CSV, texte) ou collez du texte. NOAM propose une arborescence — vous validez avant insertion." })}
            </p>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("annuaire.import_file", { defaultValue: "Fichier (PDF, XLSX, DOCX, CSV, TXT)" })}</div>
              <input type="file" accept=".pdf,.xlsx,.xlsm,.docx,.txt,.md,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block text-xs text-zinc-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#378ADD]/20 file:text-[#378ADD] file:cursor-pointer"
                data-testid="annuaire-import-file" />
              {file && <div className="text-[11px] text-zinc-500 mt-1 inline-flex items-center gap-1"><FileText className="w-3 h-3" /> {file.name}</div>}
            </label>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("annuaire.import_text", { defaultValue: "Ou collez du texte" })}</div>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6}
                placeholder={t("annuaire.import_placeholder", { defaultValue: "Coller ici un compte-rendu de réunion, un organigramme texte, une liste de contacts…" })}
                className="noam-input resize-none w-full font-mono text-xs"
                data-testid="annuaire-import-text" />
            </div>
            {err && (
              <div className="text-xs text-[#FCA5A5] p-2 rounded border border-[#E24B4A]/30 bg-[#E24B4A]/10 flex items-start gap-2">
                <AlertTriangle className="w-3 h-3 mt-0.5" /> {err}
              </div>
            )}
            {busy && progress && (
              <div className="rounded-md bg-[#378ADD]/10 border border-[#378ADD]/30 p-3 text-xs space-y-1.5" data-testid="annuaire-import-progress">
                <div className="flex items-center gap-2 text-[#378ADD] font-medium">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {t("annuaire.import_progress", { defaultValue: "NOAM analyse votre document…" })}
                  <span className="ml-auto text-zinc-400 font-normal">{progress.elapsed}s</span>
                </div>
                <div className="text-[11px] text-zinc-500">
                  {progress.diagnostics?.file?.name && <>{progress.diagnostics.file.name} · </>}
                  {progress.diagnostics?.text_length_total} caractères extraits — analyse typique 30–90 s selon la taille.
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <button onClick={onClose} className="btn-ghost text-xs">{t("common.cancel", { defaultValue: "Annuler" })}</button>
              <button onClick={analyze} disabled={busy || (!file && !text.trim())}
                className="btn-primary text-xs disabled:opacity-50" data-testid="annuaire-import-analyze">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {busy
                  ? t("annuaire.analyzing", { defaultValue: "Analyse en cours…" })
                  : t("annuaire.analyze", { defaultValue: "Analyser avec NOAM" })}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-xs text-zinc-400">
              {t("annuaire.preview_title", { defaultValue: "NOAM propose les entités suivantes. Décochez celles à ignorer." })}
            </div>
            {preview.diagnostics && (
              <div className="rounded-md bg-white/[0.02] border border-white/5 p-2.5 text-[11px] text-zinc-500 space-y-1 font-mono">
                {preview.diagnostics.file?.name && (
                  <div>{t("annuaire.diag_file", { defaultValue: "Fichier" })} : <span className="text-zinc-300">{preview.diagnostics.file.name}</span></div>
                )}
                <div>{t("annuaire.diag_chars", { defaultValue: "Texte extrait" })} : <span className="text-zinc-300">{preview.diagnostics.text_length_total} chars</span>{preview.diagnostics.chunks_processed > 1 && <span className="text-[#378ADD]"> · découpé en {preview.diagnostics.chunks_processed} blocs</span>}</div>
                <div>{t("annuaire.diag_entities", { defaultValue: "Entités détectées par NOAM" })} : <span className="text-zinc-300">{preview.count}</span></div>
              </div>
            )}
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto" data-testid="annuaire-import-preview">
              {preview.entities.length === 0 ? (
                <div className="space-y-3 py-3">
                  <div className="text-zinc-300 text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#EF9F27] mt-0.5 shrink-0" />
                    <div>
                      <div className="font-medium">{t("annuaire.preview_empty", { defaultValue: "Aucune entité détectée par NOAM" })}</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {t("annuaire.preview_empty_hint", { defaultValue: "Le texte a bien été lu (voir diagnostics ci-dessus) mais NOAM n'a identifié aucun nom propre clair. Essayez avec un document plus structuré (organigramme, liste de contacts avec en-têtes, tableau Excel)." })}
                      </div>
                    </div>
                  </div>
                  {preview.diagnostics?.text_preview && (
                    <details className="text-[11px] text-zinc-500">
                      <summary className="cursor-pointer hover:text-zinc-300">{t("annuaire.show_extracted", { defaultValue: "Voir le début du texte extrait" })}</summary>
                      <pre className="mt-2 p-2 rounded bg-black/30 border border-white/5 whitespace-pre-wrap font-mono max-h-[160px] overflow-y-auto">{preview.diagnostics.text_preview}</pre>
                    </details>
                  )}
                  {preview.raw_preview && (
                    <details className="text-[11px] text-zinc-500">
                      <summary className="cursor-pointer hover:text-zinc-300">{t("annuaire.show_claude_raw", { defaultValue: "Voir la réponse brute de NOAM" })}</summary>
                      <pre className="mt-2 p-2 rounded bg-black/30 border border-white/5 whitespace-pre-wrap font-mono max-h-[160px] overflow-y-auto">{preview.raw_preview}</pre>
                    </details>
                  )}
                </div>
              ) : preview.entities.map((e, i) => (
                <PreviewRow key={i} entity={e} index={i}
                  onToggle={() => {
                    const next = [...preview.entities];
                    next[i] = { ...next[i], _excluded: !next[i]._excluded };
                    setPreview({ ...preview, entities: next });
                  }}
                />
              ))}
            </div>
            {err && (
              <div className="text-xs text-[#FCA5A5] p-2 rounded border border-[#E24B4A]/30 bg-[#E24B4A]/10 flex items-start gap-2">
                <AlertTriangle className="w-3 h-3 mt-0.5" /> {err}
              </div>
            )}
            <div className="flex justify-between gap-2 pt-3 border-t border-white/5">
              <button onClick={() => setPreview(null)} className="btn-ghost text-xs">{t("annuaire.back", { defaultValue: "← Retour" })}</button>
              <button
                onClick={() => {
                  const filtered = { ...preview, entities: preview.entities.filter((e) => !e._excluded) };
                  setPreview(filtered);
                  confirm();
                }}
                disabled={busy || preview.entities.filter((e) => !e._excluded).length === 0}
                className="btn-primary text-xs disabled:opacity-50"
                data-testid="annuaire-import-confirm">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                {t("annuaire.confirm_import", { defaultValue: `Importer ${preview.entities.filter((e) => !e._excluded).length} entité(s)` })}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PreviewRow({ entity, onToggle }) {
  const excluded = !!entity._excluded;
  const Icon = TYPE_ICONS[entity.type] || Building2;
  return (
    <label className={`flex items-start gap-2 p-2 rounded border ${excluded ? "border-white/5 opacity-40" : "border-white/10 bg-white/[0.02]"} cursor-pointer hover:border-[#378ADD]/40`}>
      <input type="checkbox" checked={!excluded} onChange={onToggle} className="mt-0.5" />
      <Icon className="w-3.5 h-3.5 text-[#378ADD] mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-200 truncate">{entity.name}</div>
        <div className="text-[11px] text-zinc-500 flex flex-wrap gap-1.5 mt-0.5">
          <span>{entity.type}</span>
          <span>· {entity.status}</span>
          {entity.parent_label && <span>· {`parent: ${entity.parent_label}`}</span>}
          {entity.sector && <span>· {entity.sector}</span>}
          {entity.country && <span>· {entity.country}</span>}
        </div>
        {entity.description && <div className="text-[11px] text-zinc-400 mt-0.5 truncate">{entity.description}</div>}
        {(entity.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {entity.tags.map((tg, i) => (
              <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400 border border-white/10">{tg}</span>
            ))}
          </div>
        )}
      </div>
    </label>
  );
}

/* ============ CERCLE VERTUEUX ACTION BANNER (V23.14.6) ============ */
/*
 * Mini-bandeau qui relie la stratégie « Bras Armé » à l'action concrète :
 * compte les entités taggées "cercle_vertueux_potentiel" qui manquent
 * encore d'ancrage opérationnel (next_step / contact / module lié).
 * Click → ouvre la liste filtrée sur ces entités pour les compléter.
 */
function CercleVertueuxActionBanner({ onShowList }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/annuaire/cercle-vertueux/pending-actions");
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null); // strict honnêteté : on n'invente pas
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Aucun rendu si chargement initial ou s'il n'y a aucun candidat tagué
  if (loading || !data) return null;
  if ((data.total_potentiel || 0) === 0) return null;

  const pending = data.actions_pending || 0;
  // Si tout est traité → on affiche un petit état "vert" valorisant le travail accompli
  if (pending === 0) {
    return (
      <div
        className="rounded-md border border-[#1D9E75]/30 bg-[#1D9E75]/[0.08] p-3 flex items-start gap-3"
        data-testid="cercle-vertueux-banner-clean"
      >
        <div className="w-8 h-8 rounded bg-[#1D9E75]/15 border border-[#1D9E75]/30 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-4 h-4 text-[#1D9E75]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1D9E75] font-medium">
            {t("cercle_vertueux_banner.all_done_title", {
              count: data.total_potentiel,
              defaultValue: `Super Partenaires : ${data.total_potentiel} acteur(s) qualifié(s) ✓`,
            })}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {t("cercle_vertueux_banner.all_done_desc", {
              defaultValue: "Tous vos Super Partenaires / Super Clients ont une prochaine étape, un contact ou un module Noam lié. Beau travail.",
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onShowList}
      className="w-full text-left rounded-md border border-[#EFD127]/30 bg-gradient-to-r from-[#EFD127]/[0.08] to-[#EF9F27]/[0.04] p-3 flex items-start gap-3 hover:border-[#EFD127]/60 transition-colors"
      data-testid="cercle-vertueux-banner"
    >
      <div className="w-8 h-8 rounded bg-[#EFD127]/15 border border-[#EFD127]/40 flex items-center justify-center shrink-0">
        <Crown className="w-4 h-4 text-[#EFD127]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 font-medium flex items-center gap-2 flex-wrap">
          <span data-testid="cercle-vertueux-banner-count">
            {t("cercle_vertueux_banner.title", {
              count: pending,
              defaultValue: `Super Partenaires : ${pending} action${pending > 1 ? "s" : ""} à prendre`,
            })}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-[#EFD127]">{t("cercle_vertueux_banner.tag", { defaultValue: "Stratégie → Action" })}</span>
        </div>
        <div className="text-[11px] text-zinc-400 mt-0.5">
          {t("cercle_vertueux_banner.desc", {
            defaultValue: "Grands acteurs poussés ici comme Super Partenaires / Super Clients potentiels, mais sans prochaine étape, contact ou module lié. Cliquez pour les traiter.",
          })}
        </div>
        {(data.top_actions || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {data.top_actions.slice(0, 3).map((a) => (
              <span
                key={a.id}
                className="text-[10px] px-2 py-0.5 rounded bg-white/[0.04] border border-white/10 text-zinc-300 inline-flex items-center gap-1"
                data-testid={`cercle-vertueux-banner-action-${a.id}`}
              >
                <span className="truncate max-w-[160px]">{a.name}</span>
                {typeof a.score === "number" && (
                  <span className="text-[9px] text-[#EFD127] font-bold">{a.score}</span>
                )}
              </span>
            ))}
            {data.top_actions.length > 3 && (
              <span className="text-[10px] px-2 py-0.5 rounded text-zinc-500">
                +{data.top_actions.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-[#EFD127] shrink-0 mt-1" />
    </button>
  );
}

function EmptyState({ icon: Icon, title, desc, cta, onCta, testid }) {
  return (
    <div className="p-6 text-center" data-testid={testid}>
      <Icon className="w-8 h-8 text-[#378ADD] mx-auto opacity-60" />
      <div className="noam-display text-lg mt-3">{title}</div>
      <p className="text-zinc-400 text-sm mt-1 max-w-md mx-auto">{desc}</p>
      {cta && onCta && (
        <button onClick={onCta} className="btn-primary mt-4 inline-flex">
          <Plus className="w-3.5 h-3.5" /> {cta}
        </button>
      )}
    </div>
  );
}
